/**
 * Shared Analytics Logic
 *
 * Platform-agnostic implementations of analytics functions shared between
 * analytics.native.ts and analytics.web.ts. Each platform file provides
 * its own adapter (SentryAdapter / PostHogAdapter) to abstract SDK differences.
 *
 * This module MUST NOT import any platform-specific SDK (@sentry/browser,
 * @sentry/react-native, posthog-js, posthog-react-native).
 *
 * Created: 2026-02-09 (DD-426: Extract shared analytics logic)
 */

import { logger } from "../logger";
import { scrubErrorForPostHog } from "./scrubbers";

// ============================================================================
// Platform Adapter Interfaces
// ============================================================================

/**
 * Minimal Sentry interface needed by shared analytics functions.
 *
 * Uses loose typing because both @sentry/browser and @sentry/react-native
 * export namespace modules (not class instances), and their exact signatures
 * differ across SDK versions. The `any` return types prevent type conflicts
 * while maintaining structural documentation of the expected API.
 */
export interface SentryAdapter {
  captureException(error: Error, options?: any): any;
  setUser(user: any): void;
  getReplay?(): any;
}

/**
 * Minimal PostHog interface needed by shared analytics functions.
 *
 * Shared by both posthog-react-native (class instance) and
 * window.posthog (script-loaded SDK stub).
 */
export interface PostHogAdapter {
  capture(eventName: string, properties?: Record<string, any>): void;
  identify(userId: string, traits?: Record<string, any>): void;
  reset(): void;
}

// ============================================================================
// PostHog Session Linking (Sentry beforeSend helper)
// ============================================================================

/**
 * Link PostHog session context to a Sentry event for cross-platform debugging.
 *
 * Adds posthog_session_id tag and replay URL to the event for bidirectional
 * Session Replay Linking (Phase 3 - January 2026).
 *
 * @param event - Sentry event to enrich (mutated in place)
 * @param getSessionId - Platform-specific function to retrieve PostHog session ID
 *
 * @see docs/OBSERVABILITY_HUB.md - "Sentry Session Replay" section
 */
export function linkPostHogSession(
  event: any,
  getSessionId: () => string | undefined
): void {
  try {
    const posthogSessionId = getSessionId();
    if (!posthogSessionId) return;

    // Add as tags for easy filtering in Sentry UI
    event.tags = event.tags || {};
    event.tags.posthog_session_id = posthogSessionId;

    // Construct PostHog replay URL using validated format
    const posthogProjectId =
      process.env.EXPO_PUBLIC_POSTHOG_PROJECT_ID || "242748";
    const posthogHost =
      process.env.EXPO_PUBLIC_POSTHOG_UI_HOST || "us.posthog.com";
    const replayUrl = `https://${posthogHost}/project/${posthogProjectId}/replay/home?sessionRecordingId=${posthogSessionId}`;

    // Add as context for detailed debugging view (includes clickable URL)
    event.contexts = event.contexts || {};
    event.contexts.posthog = {
      session_id: posthogSessionId,
      replay_url: replayUrl,
    };
  } catch (error) {
    // Silently fail - session linking is best-effort
    logger.debug(
      "[Analytics] Failed to link PostHog session to Sentry:",
      error
    );
  }
}

// ============================================================================
// Shared captureError Implementation
// ============================================================================

/**
 * Capture error and link Sentry + PostHog with optional requestId for E2E correlation.
 *
 * Phase 3 Observability: Enhanced with requestId parameter to enable:
 * - Sentry filtering: request_id:abc-123-def
 * - PostHog correlation: request_id in event properties
 * - Database correlation: get_analysis_trace_by_id(analysis_id) -> requestId
 *
 * @param sentry - Platform-specific Sentry adapter
 * @param posthogClient - Platform-specific PostHog client (null if not initialized)
 * @param error - Error to capture
 * @param context - Additional context (optional)
 * @param requestId - Request ID from Edge Function error response (optional)
 * @returns Sentry event ID
 */
export function captureErrorShared(
  sentry: SentryAdapter,
  posthogClient: PostHogAdapter | null,
  error: Error,
  context?: Record<string, any>,
  requestId?: string
): string | undefined {
  const extra = requestId ? { ...context, request_id: requestId } : { ...context };
  const tags = requestId ? { request_id: requestId } : undefined;

  const eventId = sentry.captureException(error, { extra, tags });

  // Also send to PostHog for product analytics correlation
  if (posthogClient && eventId) {
    // Bidirectional Session Replay Linking (Phase 3 - January 2026)
    let sentryReplayId: string | undefined;
    let sentryReplayUrl: string | undefined;

    try {
      const sentryReplay = sentry.getReplay?.();
      sentryReplayId = sentryReplay?.getReplayId?.();

      if (sentryReplayId) {
        const sentryOrg = process.env.EXPO_PUBLIC_SENTRY_ORG || "liftping";
        sentryReplayUrl = `https://${sentryOrg}.sentry.io/replays/${sentryReplayId}/`;
      }
    } catch (replayError) {
      logger.debug(
        "[Analytics] Failed to get Sentry replay ID:",
        replayError
      );
    }

    // M4.2: Scrub PII from error properties before sending to PostHog
    const scrubbed = scrubErrorForPostHog(error.message, error.stack, context);

    posthogClient.capture("error_occurred", {
      error_message: scrubbed.error_message,
      error_stack: scrubbed.error_stack,
      sentry_event_id: eventId,
      sentry_replay_id: sentryReplayId,
      sentry_replay_url: sentryReplayUrl,
      request_id: requestId,
      ...scrubbed.scrubbedContext,
    });
  }

  return eventId;
}

// ============================================================================
// Shared identifyUser Implementation
// ============================================================================

/**
 * Identify user in both Sentry and PostHog analytics systems.
 */
export function identifyUserShared(
  sentry: SentryAdapter,
  posthogClient: PostHogAdapter | null,
  userId: string,
  traits?: Record<string, any>
): void {
  sentry.setUser({ id: userId, ...traits });

  if (posthogClient) {
    posthogClient.identify(userId, traits);

    // NocoDB #500: Ensure PostHog person properties include tier + subscription_status
    // Best-effort across both posthog-js (window.posthog.people.set) and posthog-react-native.
    const tier = traits?.tier ?? (traits as any)?.user_tier;
    const subscription_status =
      (traits as any)?.subscription_status ??
      (traits as any)?.subscriptionStatus ??
      (traits as any)?.subscription_state;

    const personProps: Record<string, any> = {};
    if (tier !== undefined) personProps.tier = tier;
    if (subscription_status !== undefined) personProps.subscription_status = subscription_status;

    if (Object.keys(personProps).length > 0) {
      try {
        (posthogClient as any).people?.set?.(personProps);
      } catch {}
      try {
        (posthogClient as any).setPersonProperties?.(personProps);
      } catch {}
    }
  }
}

// ============================================================================
// Shared clearUser Implementation
// ============================================================================

/**
 * Clear user identification in both Sentry and PostHog (on logout).
 */
export function clearUserShared(
  sentry: SentryAdapter,
  posthogClient: PostHogAdapter | null
): void {
  sentry.setUser(null);

  if (posthogClient) {
    posthogClient.reset();
  }
}

// ============================================================================
// Shared getTraceHeaders Implementation
// ============================================================================

/**
 * Get Sentry trace headers for distributed tracing.
 *
 * Returns headers to propagate trace context from frontend to Edge Functions:
 * - sentry-trace: Trace ID, span ID, sampling decision
 * - baggage: Dynamic sampling context (environment, release, transaction)
 *
 * @param getTraceData - Platform-specific function returning trace header data
 *
 * @see packages/drama-detector/supabase/functions/_shared/utils/sentry-edge.ts#extractTraceHeaders
 */
export function getTraceHeadersShared(
  getTraceData: () => { 'sentry-trace'?: string; baggage?: string } | undefined
): Record<string, string> {
  const headers: Record<string, string> = {};

  try {
    const traceData = getTraceData();

    if (traceData) {
      if (traceData["sentry-trace"]) {
        headers["sentry-trace"] = traceData["sentry-trace"];
      }
      if (traceData["baggage"]) {
        headers["baggage"] = traceData["baggage"];
      }
    }
  } catch (err) {
    if (__DEV__) {
      logger.log("[Analytics] Failed to get trace headers:", err);
    }
  }

  return headers;
}
